import type { Notification as NotificationType, TopMeResponse } from '@gyym/sdk'
import { ImageProps, Typography } from '@gyym/ui'
import { Clock, Fire } from '@gyym/ui/icons'
import fvLogin01PcImgSrc from '@public/assets/images/top/fv_login_01_pc.png'
import fvLogin01SpImgSrc from '@public/assets/images/top/fv_login_01_sp.png'
import fvLogin02PcImgSrc from '@public/assets/images/top/fv_login_02_pc.png'
import fvLogin02SpImgSrc from '@public/assets/images/top/fv_login_02_sp.png'
import fvLogin03PcImgSrc from '@public/assets/images/top/fv_login_03_pc.png'
import fvLogin03SpImgSrc from '@public/assets/images/top/fv_login_03_sp.png'
import fvPcImgSrc from '@public/assets/images/top/fv_pc.png'
import fvSpImgSrc from '@public/assets/images/top/fv_sp.png'

import { useFormattedNumber } from '@/hooks/useFormattedNumber'
import { useAuthUser } from '@/modules/Auth'
import { useUnreadNotificationCount } from '@/modules/Notifications'

import { TopButton } from '../TopButton'
import { ImageSwapper, ImageSwapperImage } from './ImageSwapper'
import { Notification } from './Notification'
import {
  FirstViewContainer,
  NotificationContainer,
  Pass,
  PassContainer,
  TopButtonContainer,
  TopImageContainer,
} from './styles'
import { SwitchImage } from './SwitchImage'

const fvAlt = 'GYYM - 1 TICKET FITNESS'

const fvSpImg: ImageProps = {
  src: fvSpImgSrc,
  alt: fvAlt,
  fill: true,
  priority: true,
}

const fvPcImg: ImageProps = {
  src: fvPcImgSrc,
  alt: fvAlt,
  fill: true,
  priority: true,
}

const loginImageMap: ImageSwapperImage[] = [
  {
    pc: {
      src: fvLogin01PcImgSrc,
      alt: 'BOXING',
      // height: 864,
      // width: 1920,
      fill: true,
    },
    sp: {
      src: fvLogin01SpImgSrc,
      alt: 'BOXING',
      // height: 832,
      // width: 1513,
      fill: true,
    },
  },
  {
    pc: {
      src: fvLogin02PcImgSrc,
      alt: 'YOGA',
      // height: 864,
      // width: 1920,
      fill: true,
    },
    sp: {
      src: fvLogin02SpImgSrc,
      alt: 'YOGA',
      // height: 832,
      // width: 1513,
      fill: true,
    },
  },
  {
    pc: {
      src: fvLogin03PcImgSrc,
      alt: 'MACHINE GYM',
      // height: 864,
      // width: 1920,
      fill: true,
    },
    sp: {
      src: fvLogin03SpImgSrc,
      alt: 'MACHINE GYM',
      // height: 832,
      // width: 1513,
      fill: true,
    },
  },
]

export const FirstView = ({
  me,
  notifications,
}: {
  me: TopMeResponse | undefined
  notifications: NotificationType[]
}) => {
  const { isLoggedIn, isLoading, isCorporateUser } = useAuthUser()
  const unreadNotificationCount = useUnreadNotificationCount(notifications)

  const monthlyExercisedHours = Math.floor(
    (me?.fluctuateFactor.monthlyExercisedMinutes ?? 0) / 60,
  )
  const monthlyExercisedMinutes =
    (me?.fluctuateFactor.monthlyExercisedMinutes ?? 0) % 60

  const usageNumUntilNextGrade =
    (me?.upperGrade?.monthlyReservationNumToAchieve ?? 0) -
    (me?.fluctuateFactor.monthlyReservationNum ?? 0)

  const points = useFormattedNumber(me?.point)

  return (
    <FirstViewContainer>
      <div className="relative">
        {!isLoggedIn ? (
          <h1 className="z-[-2] w-full">
            <TopImageContainer>
              <SwitchImage base={fvSpImg} tablet={fvPcImg} />
            </TopImageContainer>
          </h1>
        ) : (
          <ImageSwapper imageMap={loginImageMap} />
        )}

        {unreadNotificationCount > 0 && (
          <NotificationContainer isLoggedIn={isLoggedIn}>
            <Notification count={unreadNotificationCount} />
          </NotificationContainer>
        )}
      </div>

      {!isLoggedIn && (
        <TopButtonContainer>
          <TopButton mt={10} href="/facilities?sortBy=distance&order=asc">
            近くのジムを探す
          </TopButton>
        </TopButtonContainer>
      )}

      {/* 法人会員は GYYM PASS プログラム対象外のため非表示 */}
      {!isLoading && isLoggedIn && me && !isCorporateUser && (
        <PassContainer>
          <Pass>
            <div className="flex items-center justify-start">
              <div>
                <Typography variant="headline2" color="blue.01">
                  {me.currentGrade.name}
                </Typography>
              </div>

              <div className="ml-auto">
                <Typography variant="body2" color="blue.01">
                  {points}pt 保有
                </Typography>
              </div>
            </div>

            <div className="mt-1 flex items-center justify-start">
              <div className="flex items-center justify-start">
                <div className="mr-1 text-blue-01">
                  <Fire />
                </div>
                <Typography variant="caption1" color="blue.01">
                  利用回数 {me.fluctuateFactor.monthlyReservationNum}回 / 月
                </Typography>
              </div>

              <div className="ml-3 flex items-center justify-start">
                <div className="mr-1 text-blue-01">
                  <Clock />
                </div>
                <Typography variant="caption1" color="blue.01">
                  {monthlyExercisedHours ? `${monthlyExercisedHours}時間` : ''}
                  {monthlyExercisedMinutes
                    ? `${monthlyExercisedMinutes}分`
                    : ''}
                  {monthlyExercisedHours === 0 && monthlyExercisedMinutes === 0
                    ? '0分'
                    : ''}{' '}
                  / 月
                </Typography>
              </div>
            </div>

            {me.upperGrade && (
              <div className="mt-1">
                <Typography variant="caption1" color="gray.02">
                  {me.upperGrade.name}まであとご利用{usageNumUntilNextGrade}回
                </Typography>
              </div>
            )}
          </Pass>
        </PassContainer>
      )}
    </FirstViewContainer>
  )
}
